import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { RenderedHtml } from 'components'
import {
  fetchLearningHubBlock,
  submitLearningHubFormResponse,
  uploadLearningHubFormMedia,
} from 'api-actions'
import * as flashActions from 'redux-flash'
import { isBlockCompleted } from '../utils/block-navigation'

function savedToAnswers(savedResponses, questions) {
  const src = savedResponses || {}
  const out = {}
  questions.forEach((q) => {
    const key = String(q.id)
    const v = src[key] ?? src[q.id]
    if (v === undefined || v === null) return
    const qt = questionQt(q)
    if (qt === 'multi-select') {
      out[key] = Array.isArray(v) ? [...v] : [v]
    } else if (qt === 'rating_scale') {
      out[key] = Number(v)
    } else {
      out[key] = v
    }
  })
  return out
}

function questionQt(q) {
  return q.questionType ?? q.question_type
}

function isUploadQuestion(q) {
  const qt = questionQt(q)
  return qt === 'upload_media' || qt === 'upload'
}

const UPLOAD_AUDIO_ACCEPT = 'audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm,.flac'
const UPLOAD_VIDEO_ACCEPT = 'video/*,.mp4,.mov,.webm,.avi,.mkv'
const UPLOAD_IMAGE_ACCEPT = 'image/*,.jpg,.jpeg,.png,.gif,.webp,.heic'
const UPLOAD_MEDIA_ACCEPT = `${UPLOAD_AUDIO_ACCEPT},${UPLOAD_VIDEO_ACCEPT},${UPLOAD_IMAGE_ACCEPT}`

function questionUploadFileType(q) {
  const direct = q.uploadFileType ?? q.upload_file_type
  if (direct === 'voice' || direct === 'video') return direct

  const content = q.content
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    const nested = content.uploadFileType ?? content.upload_file_type
    if (nested === 'voice' || nested === 'video') return nested
  }

  if (Array.isArray(content) && content[0] && typeof content[0] === 'object') {
    const row = content[0]
    const nested = row.uploadFileType ?? row.upload_file_type
    if (nested === 'voice' || nested === 'video') return nested
  }

  return null
}

function uploadAcceptForType(fileType) {
  if (fileType === 'voice') return UPLOAD_AUDIO_ACCEPT
  if (fileType === 'video') return UPLOAD_VIDEO_ACCEPT
  return UPLOAD_MEDIA_ACCEPT
}

function isAllowedUploadFile(file, fileType) {
  const mime = (file.type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()

  if (fileType === 'voice') {
    return (
      mime.startsWith('audio/') ||
      /\.(mp3|wav|m4a|aac|ogg|webm|flac)$/.test(name)
    )
  }
  if (fileType === 'video') {
    return mime.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv)$/.test(name)
  }

  return (
    mime.startsWith('audio/') ||
    mime.startsWith('video/') ||
    mime.startsWith('image/') ||
    /\.(mp3|wav|m4a|aac|ogg|webm|flac|mp4|mov|avi|mkv|jpg|jpeg|png|gif|webp|heic)$/.test(
      name
    )
  )
}

function uploadValidationMessage(fileType) {
  if (fileType === 'voice') {
    return 'Please upload a voice recording (e.g. mp3, wav, m4a).'
  }
  if (fileType === 'video') {
    return 'Please upload a video file (e.g. mp4, mov).'
  }
  return 'Please upload a voice recording, video, or image file.'
}

function uploadButtonLabel(fileType, uploading) {
  if (uploading) return 'Uploading…'
  if (fileType === 'voice') return 'Upload Voice'
  if (fileType === 'video') return 'Upload Video'
  return 'Upload Media'
}

/** API / camelCase may vary; option rows must be `{ id, title }` for the UI. */
function coerceOptionsList(options) {
  if (!Array.isArray(options)) return []
  return options
    .map((opt) => {
      if (!opt || typeof opt !== 'object') return null
      const idRaw =
        opt.id ?? opt.optionId ?? opt.option_id ?? opt.value ?? opt.key
      const titleRaw = opt.title ?? opt.label ?? opt.name ?? opt.text ?? ''
      const id =
        idRaw != null && String(idRaw).trim() !== '' ? String(idRaw) : ''
      const title = titleRaw != null ? String(titleRaw) : ''
      if (!id && !title) return null
      return { id: id || title, title: title || id }
    })
    .filter(Boolean)
}

/** Rating rows keep `scale` (1–5); empty labels fall back to the number in the UI. */
function coerceRatingOptions(options) {
  const rows = Array.isArray(options) ? options : []
  const mapped = rows.map((opt, i) => {
    if (!opt || typeof opt !== 'object') {
      return { scale: i + 1, id: String(i + 1), title: '' }
    }
    const scaleRaw = opt.scale ?? opt.Scale
    let scale = Number(scaleRaw)
    if (!Number.isFinite(scale) || scale < 1 || scale > 5) {
      scale = i + 1
    }
    const idRaw = opt.id ?? opt.optionId ?? opt.option_id
    const id =
      idRaw != null && String(idRaw).trim() !== ''
        ? String(idRaw)
        : String(scale)
    const titleRaw = opt.title ?? opt.label ?? opt.name ?? ''
    const title = titleRaw != null ? String(titleRaw).trim() : ''
    return { scale, id, title }
  })

  if (mapped.length === 0) {
    return [1, 2, 3, 4, 5].map((n) => ({ scale: n, id: String(n), title: '' }))
  }

  const trimmed = mapped.length > 5 ? mapped.slice(0, 5) : mapped
  if (trimmed.length >= 5) return trimmed

  const byScale = new Map()
  trimmed.forEach((r) => {
    const s = r.scale
    if (Number.isFinite(s) && s >= 1 && s <= 5) byScale.set(s, r)
  })
  return [1, 2, 3, 4, 5].map((n) => {
    const existing = byScale.get(n)
    if (existing) return existing
    return { scale: n, id: String(n), title: '' }
  })
}

function isAnswered(q, value) {
  const qt = questionQt(q)
  if (qt === 'single-select') {
    return typeof value === 'string' && value.trim().length > 0
  }
  if (qt === 'multi-select') {
    return Array.isArray(value) && value.some((x) => String(x).trim())
  }
  if (qt === 'rating_scale') {
    return Number.isFinite(value) && value >= 1 && value <= 5
  }
  if (qt === 'open-ended' || isUploadQuestion(q)) {
    return typeof value === 'string' && value.trim().length > 0
  }
  return false
}

function buildPayloadAnswers(questions, answers) {
  const out = {}
  questions.forEach((q) => {
    const key = String(q.id)
    const qt = questionQt(q)
    const raw = answers[key]
    if (qt === 'multi-select') {
      out[key] = Array.isArray(raw) ? raw.map((x) => String(x)).sort() : []
    } else if (qt === 'rating_scale') {
      out[key] = raw
    } else {
      out[key] = raw
    }
  })
  return out
}

function getOriginalQuestionIndex(row, questions, fallbackIndex) {
  const rawId = row?.questionId ?? row?.question_id
  if (rawId == null) return fallbackIndex + 1
  const id = String(rawId)
  const matchIndex = questions.findIndex((q) => String(q.id) === id)
  return matchIndex >= 0 ? matchIndex + 1 : fallbackIndex + 1
}

function resolveQuestionOptionTitle(question, token) {
  if (!question || !Array.isArray(question.options)) return token
  const normalizedToken = String(token || '').trim()
  if (!normalizedToken) return normalizedToken
  const found = question.options.find(
    (opt) => String(opt?.id || '').trim() === normalizedToken
  )
  return found?.title || normalizedToken
}

function formatReviewAnswerText(row, questions, fallbackValue) {
  const rawQuestionId = row?.questionId ?? row?.question_id
  const question = questions.find((q) => String(q.id) === String(rawQuestionId))
  const raw = fallbackValue == null ? '' : String(fallbackValue)
  if (!raw) return ''

  // Split only on comma+space list delimiters so labels like "10,000" stay intact.
  const pieces = raw
    .split(/,\s+/)
    .map((piece) => piece.trim())
    .filter(Boolean)
  if (pieces.length <= 1)
    return resolveQuestionOptionTitle(question, raw.trim())

  return pieces
    .map((piece) => resolveQuestionOptionTitle(question, piece))
    .join('\n')
}

function LearningHubFormBlockPanel({
  block,
  onNavigateAfterSuccess,
  courseId,
  fetchLearningHubCourse,
  fetchLearningHubBlock,
  flashErrorMessage,
}) {
  const questions = useMemo(() => {
    const raw = block.questions
    if (!Array.isArray(raw)) return []
    return [...raw]
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((q) => ({
        ...q,
        options:
          questionQt(q) === 'rating_scale'
            ? coerceRatingOptions(q.options)
            : coerceOptionsList(q.options),
      }))
  }, [block.questions, block.id])

  const savedResponses = block.savedResponses || block.saved_responses || {}
  const submitForReview = Boolean(
    block.submitForReview ?? block.submit_for_review
  )
  const retestRequired = Boolean(block.retestRequired ?? block.retest_required)
  const retestPercentage = Number(
    block.retestPercentage ?? block.retest_percentage ?? 0
  )
  const reviewerComment = (
    block.reviewerComment ??
    block.reviewer_comment ??
    ''
  )
    .toString()
    .trim()

  const [answers, setAnswers] = useState(() =>
    savedToAnswers(savedResponses, questions)
  )
  const [submitting, setSubmitting] = useState(false)
  const [uploadingId, setUploadingId] = useState(null)
  const [quizModal, setQuizModal] = useState(null)

  useEffect(() => {
    setAnswers(savedToAnswers(savedResponses, questions))
  }, [block.id, savedResponses, questions])

  const allComplete = useMemo(
    () => questions.every((q) => isAnswered(q, answers[String(q.id)])),
    [questions, answers]
  )

  const isReadOnly = isBlockCompleted(block)

  const advance = useCallback(async () => {
    await fetchLearningHubBlock(block.id)
    await onNavigateAfterSuccess()
  }, [block.id, fetchLearningHubBlock, onNavigateAfterSuccess])

  const postIntent = async (intent, answerOverride) => {
    const payloadAnswers =
      answerOverride !== undefined
        ? answerOverride
        : buildPayloadAnswers(questions, answers)
    return submitLearningHubFormResponse(block.id, {
      intent,
      answers: payloadAnswers,
    })
  }

  const handleSaveDraft = async () => {
    setSubmitting(true)
    try {
      await postIntent('save_draft')
      await fetchLearningHubBlock(block.id)
    } catch (err) {
      flashErrorMessage(err?.message || 'Unable to save draft.')
    } finally {
      setSubmitting(false)
    }
  }

  const handlePrimarySubmit = async () => {
    if (!allComplete) {
      flashErrorMessage('Please answer all questions before continuing.')
      return
    }
    const intent = submitForReview ? 'submit_for_review' : 'submit'
    setSubmitting(true)
    try {
      const data = await postIntent(intent)
      if (data?.draft) {
        await fetchLearningHubBlock(block.id)
        return
      }
      const graded = Boolean(data?.graded)
      const wrongItems = data?.wrongItems ?? data?.wrong_items ?? []
      if (!graded || wrongItems.length === 0) {
        await advance()
        return
      }
      await fetchLearningHubBlock(block.id)
      setQuizModal({
        wrongItems,
        canContinue: data?.canContinue ?? data?.can_continue ?? true,
        retestRequired,
        retestPercentage,
      })
    } catch (err) {
      flashErrorMessage(err?.message || 'Unable to submit form.')
    } finally {
      setSubmitting(false)
    }
  }

  const closeQuizModal = () => setQuizModal(null)

  const handleTryAgain = async () => {
    setSubmitting(true)
    try {
      await postIntent('retest', {})
      setAnswers({})
      closeQuizModal()
      await fetchLearningHubBlock(block.id)
      await fetchLearningHubCourse(courseId)
    } catch (err) {
      flashErrorMessage(err?.message || 'Unable to reset form for retest.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleQuizContinue = async () => {
    if (!quizModal?.canContinue) return
    closeQuizModal()
    await advance()
  }

  const setQuestionValue = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [String(qid)]: value }))
  }

  const toggleMulti = (qid, optionId, checked) => {
    setAnswers((prev) => {
      const key = String(qid)
      const cur = Array.isArray(prev[key]) ? [...prev[key]] : []
      const id = String(optionId)
      if (checked) {
        if (!cur.includes(id)) cur.push(id)
      } else {
        const idx = cur.indexOf(id)
        if (idx >= 0) cur.splice(idx, 1)
      }
      return { ...prev, [key]: cur }
    })
  }

  const onPickUpload = async (question, files) => {
    const file = files && files[0]
    if (!file) return
    const qid = question.id
    const fileType = questionUploadFileType(question)
    if (!isAllowedUploadFile(file, fileType)) {
      flashErrorMessage(uploadValidationMessage(fileType))
      return
    }
    setUploadingId(qid)
    try {
      const url = await uploadLearningHubFormMedia(block.id, file)
      setQuestionValue(qid, url)
    } catch (err) {
      flashErrorMessage(
        err?.message || 'Unable to upload file. Please try again.'
      )
    } finally {
      setUploadingId(null)
    }
  }

  const desc = block.description

  return (
    <div
      className={`learning-hub-block-player__panel learning-hub-block-player__panel--form${
        isReadOnly ? ' learning-hub-form-block--read-only' : ''
      }`}
    >
      {reviewerComment ? (
        <div className="learning-hub-form-block__admin-comment" role="note">
          <strong>Admin Comment:</strong>
          <br /> {reviewerComment}
        </div>
      ) : null}
      {desc ? (
        <div className="learning-hub-block-player__block-description">
          <RenderedHtml>{desc}</RenderedHtml>
        </div>
      ) : null}

      <div className="learning-hub-form-block__questions">
        {questions.map((q, idx) => {
          const qid = String(q.id)
          const qt = questionQt(q)
          const label = `${idx + 1}. ${q.questionText ?? q.question_text}`
          const options = q.options || []
          const uploadFileType = isUploadQuestion(q)
            ? questionUploadFileType(q)
            : null

          return (
            <div key={qid} className="learning-hub-form-block__card">
              <div className="learning-hub-form-block__question-title">
                {label}
              </div>
              {qt === 'single-select' &&
                options.map((opt) => (
                  <label
                    key={opt.id || opt.title}
                    className="learning-hub-form-block__choice"
                  >
                    <input
                      type="radio"
                      name={`lh-form-q-${qid}`}
                      checked={String(answers[qid] ?? '') === String(opt.id)}
                      disabled={isReadOnly}
                      onChange={() => setQuestionValue(qid, opt.id)}
                    />
                    <span>{opt.title}</span>
                  </label>
                ))}
              {qt === 'multi-select' &&
                options.map((opt) => {
                  const selected = Array.isArray(answers[qid])
                    ? answers[qid]
                    : []
                  const checked = selected.map(String).includes(String(opt.id))
                  return (
                    <label
                      key={opt.id || opt.title}
                      className="learning-hub-form-block__choice learning-hub-form-block__choice--check"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          toggleMulti(q.id, opt.id, e.target.checked)
                        }
                      />
                      <span>{opt.title}</span>
                    </label>
                  )
                })}
              {qt === 'open-ended' && (
                <textarea
                  className="learning-hub-form-block__textarea"
                  rows={5}
                  placeholder="Description…"
                  value={answers[qid] ?? ''}
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                  onChange={(e) => setQuestionValue(qid, e.target.value)}
                />
              )}
              {qt === 'rating_scale' && (
                <div className="learning-hub-form-block__rating">
                  {options.map((opt, idx) => {
                    const scale = Number(opt.scale)
                    const num =
                      Number.isFinite(scale) && scale >= 1 && scale <= 5
                        ? scale
                        : idx + 1
                    const label =
                      opt.title && String(opt.title).trim() !== ''
                        ? String(opt.title).trim()
                        : String(num)
                    return (
                      <div
                        key={`${qid}-${num}-${opt.id || idx}`}
                        className="learning-hub-form-block__rating-cell"
                      >
                        <input
                          type="radio"
                          name={`lh-form-rating-${qid}`}
                          checked={Number(answers[qid]) === num}
                          disabled={isReadOnly}
                          onChange={() => setQuestionValue(qid, num)}
                        />
                        <span className="learning-hub-form-block__rating-num">
                          {label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
              {isUploadQuestion(q) && (
                <div className="learning-hub-form-block__upload">
                  <input
                    type="file"
                    className="learning-hub-form-block__file-input"
                    id={`lh-form-upload-${qid}`}
                    accept={uploadAcceptForType(uploadFileType)}
                    onChange={(e) => {
                      onPickUpload(q, e.target.files)
                      e.target.value = ''
                    }}
                  />
                  <button
                    type="button"
                    className="learning-hub-form-block__upload-btn button"
                    disabled={isReadOnly || uploadingId === q.id}
                    onClick={() =>
                      document.getElementById(`lh-form-upload-${qid}`)?.click()
                    }
                  >
                    {uploadButtonLabel(uploadFileType, uploadingId === q.id)}
                  </button>
                  {answers[qid] ? (
                    <p className="learning-hub-form-block__upload-done">
                      File attached.
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {!isReadOnly ? (
        <div className="learning-hub-form-block__footer">
          <button
            type="button"
            className="learning-hub-form-block__btn learning-hub-form-block__btn--outline"
            disabled={submitting}
            onClick={handleSaveDraft}
          >
            Save draft
          </button>
          <button
            type="button"
            className="learning-hub-form-block__btn learning-hub-form-block__btn--primary"
            disabled={submitting || !allComplete}
            onClick={handlePrimarySubmit}
          >
            {submitForReview ? 'Submit for review' : 'Continue'}
          </button>
        </div>
      ) : null}

      {!isReadOnly && quizModal ? (
        <div
          className="learning-hub-form-block__modal-backdrop"
          role="presentation"
          onClick={closeQuizModal}
        >
          <div
            className="learning-hub-form-block__modal"
            role="dialog"
            aria-modal
            aria-labelledby="lh-quiz-review-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="lh-quiz-review-title">Thanks for completing the quiz!</h3>
            <p className="learning-hub-form-block__modal-sub">
              You&apos;ve submitted your responses. Some answers weren&apos;t
              quite aligned with the recommended guidance - we&apos;ve
              highlighted those so you can review and learn more.
            </p>
            <table className="learning-hub-form-block__modal-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Correct Answer</th>
                  <th>Your Answer</th>
                </tr>
              </thead>
              <tbody>
                {(quizModal.wrongItems || []).map((row, idx) => (
                  <tr key={row.questionId ?? row.question_id}>
                    <td>{`Question ${getOriginalQuestionIndex(
                      row,
                      questions,
                      idx
                    )}`}</td>
                    <td>
                      {formatReviewAnswerText(
                        row,
                        questions,
                        row.correctAnswerDisplay ?? row.correct_answer_display
                      )}
                    </td>
                    <td className="learning-hub-form-block__modal-wrong">
                      {formatReviewAnswerText(
                        row,
                        questions,
                        row.userAnswerDisplay ?? row.user_answer_display
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="learning-hub-form-block__modal-actions">
              <button
                type="button"
                className="learning-hub-form-block__btn learning-hub-form-block__btn--muted"
                disabled={submitting}
                onClick={handleTryAgain}
              >
                Try it again
              </button>
              <button
                type="button"
                className="learning-hub-form-block__btn learning-hub-form-block__btn--primary"
                disabled={!quizModal.canContinue}
                onClick={handleQuizContinue}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

LearningHubFormBlockPanel.propTypes = {
  block: PropTypes.object.isRequired,
  onNavigateAfterSuccess: PropTypes.func.isRequired,
  courseId: PropTypes.number.isRequired,
  fetchLearningHubCourse: PropTypes.func.isRequired,
  fetchLearningHubBlock: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
}

export default compose(
  connect(null, {
    fetchLearningHubBlock,
    flashErrorMessage: flashActions.flashErrorMessage,
  })
)(LearningHubFormBlockPanel)
